import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import ContactContainer from '../../components/contacts';
import ContactListContainer from '../../components/contacts/contactList';
import { IAuthContext } from '../../context/auth/auth-context';
import withAuthContext from '../../context/auth/AuthConsumer';
import { CeresLogger } from '../../logger';
import { Users } from 'react-feather';
import { API_BASE_URL } from '../../util/api.util';
import ReactGA from 'react-ga4';
import { Card, Kicker, SearchInput } from '../../components/ui';

const PageRoot = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ContentInner = styled.div`
  margin: 4em 5em;
  box-sizing: border-box;
`;

const FactoidsCard = styled(Card)`
  background: rgba(7, 10, 13, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.06);;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(10px);

  h3 {
    margin: 0;
  }
`;

const StatBlock = styled.div`
  text-align: left;
  padding: 0;
  border: none;
  background: transparent;
`;

const StatValue = styled.div`
  font-size: 28px;
  line-height: 1;
  color: #34d399;
  margin-bottom: 0.55rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.div`
  font-size: 12px;
    color: #546170;
    font-family: 'JetBrains Mono', monospace;
    margin-top: 3px;
`;

const HeroBlock = styled.div`
  text-align: center;
`;

const PageTitle = styled.h1`
  margin: 0 0 0.35rem;
  font-size: clamp(2rem, 4.5vw, 3rem);
  font-weight: 400;
  line-height: 1.15;
  color: #fafafa;
  font-family: 'Instrument Serif', Georgia, serif;
`;

const PageSubtitle = styled.h2`
  margin: 0 0 1.5rem;
  font-size: clamp(1rem, 2vw, 1.25rem);
  font-weight: 500;
  color: #34d399;
  font-style: italic;
  font-family: 'Instrument Serif', Georgia, serif;
`;

const SearchForm = styled.form`
  width: 100%;
  max-width: 640px;
  margin: 0 auto 0.5rem;
`;

const TrackingList = styled.div`
  margin-top: 20px;
`;

const CustRow = styled(Row)`
  align-items: center;
`;

const DetailHeaderIcon = styled.div`
  display: flex;
  justify-content: center;
  color: #34d399;

  svg {
    filter: drop-shadow(0 4px 12px rgba(52, 211, 153, 0.25));
  }
`;

const ContactDiv = styled.div`
  text-align: center;

  h3 {
    margin: 0 0 0.25rem;
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 600;
    color: #f8fafc;
    font-family: 'Instrument Serif', Georgia, serif;
  }

  h6 {
    margin: 0;
    font-size: clamp(1rem, 2vw, 1.15rem);
    font-weight: 500;
    color: #5eead4;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
  }
`;

const SubscribeRNFormContainer = styled.div`
  form {
    margin-top: 0;
  }
`;

type SortOrder = 'asc' | 'desc';
type ActiveOrder = 'company' | 'other';

const PeopleFinderPage: React.FC<{ context: IAuthContext }> = () => {
  const [peopleFinderSearch, setPeopleFinderSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [orderLocation, setOrderLocation] = useState<SortOrder>('asc');
  const [orderCompany, setOrderCompany] = useState<SortOrder>('desc');
  const [activeOrder, setActiveOrder] = useState<ActiveOrder>('company');
  const [isLoad, setLoad] = useState(false);
  const [showFrom, setShowFrom] = useState(0);
  const [peopleList, setPeopleList] = useState<any>([]);
  const [selectedContactList, setSelectedContactList] = useState<any>([]);
  const [showContactList, setShowContactList] = useState<boolean>(false);
  const [contactCount, setContactCount] = useState<any>(0);
  const showItems = 20;

  const getContactTotalCount = async () => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
      };
      const data = {
        action: 'total'
      };
      const totalContactsResp = await axios.post(API_BASE_URL + '/TCEQGetContacts', data, config);
      if (totalContactsResp?.data?.total_contacts) {
        setContactCount(totalContactsResp.data.total_contacts);
      }
    } catch (error) {
      toast.error(`Failed to load contact count.`);
    }
  };

  useEffect(() => {
    getContactTotalCount();
  }, []);

  const getPeopleList = async (fromPage = 0) => {
    try {
      setIsLoading(true);
      ReactGA.event({
        category: 'People Finder',
        action: 'People Finder Search',
        label: peopleFinderSearch
      });
      const config = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
      };
      const data = {
        action: 'search',
        search_type: 'all',
        keyword: peopleFinderSearch,
        records_num_start: fromPage,
        records_num_show: showItems,
        sorting: activeOrder === 'company' ? 'company-' + orderCompany : ''
      };
      await axios
        .post(API_BASE_URL + '/TCEQGetContacts', data, config)
        .then((response: any) => {
            if(response.data && response.data) {
              setPeopleList({
                ...response.data
              });
            }
        })
        .catch((error) => {
            CeresLogger.error(error);
            toast.error(`Failed to load contact list.`);
        })
        .finally(() => setIsLoading(false));

    } catch (error) {
      toast.error(`Failed to load contact list.`);
    }
  };

  const onContactListReq = (row: any) => {
    setSelectedContactList({
        data: row.contacts,
        ...row
    });
    setShowContactList(true);
  };

  return (
    <PageRoot className="people-finder-page">
      <ContentInner>
        <SubscribeRNFormContainer className="sub-track">
          {showContactList ? (
            <CustRow className="mb-4">
              <Col md={3} className="mb-3 mb-md-0">
                <DetailHeaderIcon>
                  <Users size={120} strokeWidth={1.25} aria-hidden />
                </DetailHeaderIcon>
              </Col>
              <Col md={9}>
                <ContactDiv>
                  <h3>{selectedContactList.entity_name}</h3>
                  <h6>{selectedContactList.city}</h6>
                </ContactDiv>
              </Col>
            </CustRow>
          ) : (
            <CustRow className="mb-4">
              <Col lg={4} className="mb-4 mb-lg-0">
                <FactoidsCard>
                  <Kicker align="left">Contacts in EnviroA</Kicker>
                  <StatBlock>
                    <StatValue>{contactCount}</StatValue>
                    <StatLabel>Total email / content ID</StatLabel>
                  </StatBlock>
                </FactoidsCard>
              </Col>
              <Col lg={8}>
                <HeroBlock>
                  <PageTitle>People Finder</PageTitle>
                  <PageSubtitle>from environmental agency files</PageSubtitle>
                </HeroBlock>
                <SearchForm
                  noValidate
                  onSubmit={(e: React.FormEvent) => {
                    e.preventDefault();
                    const form = e.currentTarget as HTMLFormElement;
                    if (form && !form.checkValidity()) {
                      e.stopPropagation();
                      return;
                    }
                    setShowFrom(0);
                    getPeopleList(0);
                  }}
                >
                  <SearchInput
                    value={peopleFinderSearch}
                    onChange={setPeopleFinderSearch}
                    onSubmit={() => {
                      setShowFrom(0);
                      getPeopleList(0);
                    }}
                    loading={isLoading}
                    placeholder="Search company, facility, location, person…"
                    ariaLabel="Search"
                    variant="hero"
                    size="lg"
                  />
                </SearchForm>
              </Col>
            </CustRow>
          )}
          <TrackingList>
            {showContactList ? (
              <ContactListContainer
                rows={selectedContactList}
                hideContactListTable={() => setShowContactList(false)}
              />
            ) : (
              <ContactContainer
                getPeopleList={getPeopleList}
                showItems={showItems}
                isLoad={isLoad}
                orderCompany={orderCompany}
                setLoad={setLoad}
                setOrderCompany={setOrderCompany}
                setOrderLocation={setOrderLocation}
                activeOrder={activeOrder}
                setActiveOrder={setActiveOrder}
                orderLocation={orderLocation}
                rows={peopleList}
                onContactListReq={onContactListReq}
                showFrom={showFrom}
                setShowFrom={setShowFrom}
              />
            )}
          </TrackingList>
        </SubscribeRNFormContainer>
      </ContentInner>
    </PageRoot>
  );
};

export default withAuthContext(PeopleFinderPage);
